/**
 * The `I18nProvider` implementation (Deliverable A + C) — §6.5.
 *
 * Resolves a `MessageId` to a localized string via the locale's `Catalog`, using
 * `@formatjs/intl-messageformat` to compile ICU patterns (plural/select/number/date) and
 * caching compiled formatters per (locale, id). `dir`/`format`/`fontStack`/`hasCatalog`
 * round out the surface. Strings come from ids + catalogs only, so adding a language is
 * purely registering another `Catalog`.
 *
 * INV-STABLE-ID: this provider is the *only* place ids become strings. The verdict never
 * passes through here — swapping a catalog changes what `t` returns and nothing else.
 */
import { IntlMessageFormat } from 'intl-messageformat';
import { directionOf, primarySubtag } from './bidi.js';
import { fontStackFor } from './font-stacks.js';
import { scriptOf } from './script.js';
/**
 * The `language-script` subtag of a locale, lower-cased, when a script subtag is present:
 * `zh-Hant-HK` → `zh-hant`, `sr-Latn` → `sr-latn`, `en-US` → `undefined` (region, not script).
 * Used so a script variant resolves to its own catalog before collapsing to the bare language.
 */
function languageScriptKey(locale) {
    const parts = locale.split('-');
    for (const part of parts.slice(1)) {
        if (/^[A-Za-z]{4}$/.test(part))
            return `${primarySubtag(locale)}-${part.toLowerCase()}`;
    }
    return undefined;
}
const DEFAULT_MISSING = (id) => `«${id}»`;
export class CatalogI18nProvider {
    catalogs = new Map();
    fallbackLocale;
    onMissing;
    /** Compiled-formatter cache, keyed `resolvedLocale id`. */
    compiled = new Map();
    constructor(catalogs, opts = {}) {
        for (const c of catalogs)
            this.register(c);
        this.fallbackLocale = opts.fallbackLocale ?? 'en';
        this.onMissing = opts.onMissing ?? DEFAULT_MISSING;
    }
    /** Add or replace a catalog (the catalog-only language model). */
    register(catalog) {
        this.catalogs.set(catalog.locale.toLowerCase(), catalog);
        // Drop any cached formatters for this locale so a re-register takes effect.
        const prefix = `${catalog.locale.toLowerCase()} `;
        for (const key of this.compiled.keys()) {
            if (key.startsWith(prefix))
                this.compiled.delete(key);
        }
    }
    /**
     * Resolve a locale to a present catalog, script-subtag aware (Deliverable C):
     * exact match → language+script (`zh-Hant-HK` → `zh-hant`) → primary subtag
     * (`en-US` → `en`) → configured fallback. So a script variant selects its own catalog
     * rather than collapsing straight to the primary language. Returns `undefined` only if
     * even the fallback is unregistered.
     */
    resolveCatalog(locale) {
        const lower = locale.toLowerCase();
        const langScript = languageScriptKey(locale);
        return (this.catalogs.get(lower) ??
            (langScript ? this.catalogs.get(langScript) : undefined) ??
            this.catalogs.get(primarySubtag(locale)) ??
            this.catalogs.get(this.fallbackLocale.toLowerCase()) ??
            this.catalogs.get(primarySubtag(this.fallbackLocale)));
    }
    /** The configured fallback locale's catalog, if registered (graceful partial support). */
    fallbackCatalog() {
        return (this.catalogs.get(this.fallbackLocale.toLowerCase()) ??
            this.catalogs.get(primarySubtag(this.fallbackLocale)));
    }
    t(id, params, locale) {
        const key = id;
        const catalog = this.resolveCatalog(locale);
        if (!catalog)
            return this.onMissing(String(id), locale);
        // Graceful partial support (Deliverable E): the matched catalog wins, but a catalog
        // that covers only some ids still works -- a missing id falls back to the configured
        // fallback locale's catalog, and only if THAT also lacks it does the visible « id »
        // marker render. INV-STABLE-ID holds: this only changes which string `t` returns.
        let source = catalog;
        let pattern = catalog.messages[key];
        if (pattern === undefined) {
            const fb = this.fallbackCatalog();
            if (fb && fb !== catalog && fb.messages[key] !== undefined) {
                source = fb;
                pattern = fb.messages[key];
            }
        }
        if (pattern === undefined)
            return this.onMissing(String(id), locale);
        const cacheKey = `${source.locale.toLowerCase()} ${String(id)}`;
        try {
            let fmt = this.compiled.get(cacheKey);
            if (!fmt) {
                fmt = new IntlMessageFormat(pattern, source.locale);
                this.compiled.set(cacheKey, fmt);
            }
            // intl-messageformat returns string | parts; for our string-only patterns it is a string.
            const out = fmt.format(params);
            return typeof out === 'string' ? out : String(out);
        }
        catch {
            // A malformed or hostile ICU pattern (or a format error) stays a presentation
            // gap, never a thrown crash of the render path — it degrades to the miss
            // rendering, the same way an absent id does. (Security audit F2; matters most
            // once untrusted/community catalogs become loadable.)
            return this.onMissing(String(id), locale);
        }
    }
    dir(locale) {
        // Prefer the catalog's declared direction; fall back to (script-subtag-aware) direction.
        const langScript = languageScriptKey(locale);
        const catalog = this.catalogs.get(locale.toLowerCase()) ??
            (langScript ? this.catalogs.get(langScript) : undefined) ??
            this.catalogs.get(primarySubtag(locale));
        return catalog?.dir ?? directionOf(locale);
    }
    format(value, locale, opts) {
        if (value instanceof Date) {
            return new Intl.DateTimeFormat(locale, opts).format(value);
        }
        return new Intl.NumberFormat(locale, opts).format(value);
    }
    fontStack(script) {
        return fontStackFor(script);
    }
    /**
     * The writing system for a locale (Deliverable A + C), script-subtag aware. The L4 seam
     * pairs it with `fontStack(...)` to render a chosen language in its proper script. Pure
     * presentation: it never touches a verdict.
     */
    scriptOf(locale) {
        return scriptOf(locale);
    }
    hasCatalog(locale) {
        const langScript = languageScriptKey(locale);
        return (this.catalogs.has(locale.toLowerCase()) ||
            (langScript !== undefined && this.catalogs.has(langScript)) ||
            this.catalogs.has(primarySubtag(locale)));
    }
    /** Registered locales (introspection / tests). */
    locales() {
        return Array.from(this.catalogs.values(), (c) => c.locale);
    }
}
/** Convenience factory. */
export function createI18nProvider(catalogs, opts) {
    return new CatalogI18nProvider(catalogs, opts);
}
//# sourceMappingURL=provider.js.map